// NEW: UI_REVAMP
import { Button, Flex, Select, Text, useMediaQuery } from '@chakra-ui/react';

export const buildPaginationItems = (currentPage, totalPages) => {
	if (totalPages <= 6) {
		return Array.from({ length: totalPages }, (_, index) => index + 1);
	}

	if (currentPage <= 3) {
		return [1, 2, 3, 4, 'ellipsis', totalPages];
	}

	if (currentPage >= totalPages - 2) {
		return [1, 'ellipsis', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
	}

	return [1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages];
};

export const buildCompactPaginationItems = (currentPage, totalPages, visibleCount = 3) => {
	if (totalPages <= visibleCount) {
		return Array.from({ length: totalPages }, (_, index) => index + 1);
	}

	const halfWindow = Math.floor(visibleCount / 2);
	let startPage = currentPage - halfWindow;
	let endPage = currentPage + halfWindow;

	if (visibleCount % 2 === 0) {
		endPage -= 1;
	}

	if (startPage < 1) {
		startPage = 1;
		endPage = visibleCount;
	}

	if (endPage > totalPages) {
		endPage = totalPages;
		startPage = totalPages - visibleCount + 1;
	}

	return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
};

const getScrollContainer = (element) => {
	const pageScrollContainer = document.querySelector('[data-page-scroll-container="true"]');

	if (pageScrollContainer) return pageScrollContainer;

	let parent = element?.parentElement;

	while (parent) {
		const overflowY = window.getComputedStyle(parent).overflowY;

		if (/(auto|scroll)/.test(overflowY) && parent.scrollHeight > parent.clientHeight) {
			return parent;
		}

		parent = parent.parentElement;
	}

	return document.scrollingElement;
};

const scrollToPageTop = (scrollContainer) => {
	requestAnimationFrame(() => {
		scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
	});
};

const PaginationButton = ({
	label,
	onClick,
	isDisabled = false,
	isActive = false,
	isCompact = false,
}) => {
	return (
		<Button
			onClick={onClick}
			isDisabled={isDisabled}
			minWidth={isCompact ? '28px' : '36px'}
			height={isCompact ? '28px' : '36px'}
			paddingX={isCompact ? '0' : '12px'}
			borderRadius={isCompact ? '6px' : '8px'}
			border='none'
			background={isActive ? '#3C82F6' : '#8774FE33'}
			color={isActive ? '#FFFFFF' : '#292929'}
			fontSize={isCompact ? '12px' : '14px'}
			fontWeight='500'
			letterSpacing={0}
			boxShadow='none'
			_hover={{
				background: isActive ? '#3C82F6' : '#CEC5F5',
			}}
			_active={{
				background: isActive ? '#3C82F6' : '#C5BCF1',
			}}
			_disabled={{
				opacity: 0.55,
				background: '#8774FE33',
				color: '#8E84B8',
			}}
		>
			{label}
		</Button>
	);
};

const DesktopPagination = ({
	pageNum,
	pageSize,
	totalEntries,
	startEntry,
	endEntry,
	totalPages,
	paginationItems,
	isFetching,
	onPageChange,
	onPageSizeChange,
	pageSizeOptions = [3, 6, 9],
}) => {
	const [isMobilePagination] = useMediaQuery('(max-width: 1023px)');
	const handlePageChange = (nextPage, event) => {
		if (nextPage === pageNum) return;

		const scrollContainer = getScrollContainer(event.currentTarget);

		onPageChange(nextPage);
		scrollToPageTop(scrollContainer);
	};

	if (totalEntries <= 0) return null;

	return (
		<Flex
			marginTop='8px'
			padding={{ base: '12px 0', lg: '12px 16px' }}
			alignItems='center'
			justifyContent='space-between'
			flexDirection='row'
			borderRadius='16px'
			gap={{ base: '8px', lg: '16px' }}
			width='100%'
		>
			<Flex alignItems='center' gap='16px' flexWrap='wrap' width='auto' flex='1' minWidth='0'>
				<Text
					fontSize={{ base: '12px', lg: '14px' }}
					fontWeight='400'
					letterSpacing={0}
					color='#292929'
					whiteSpace='nowrap'
				>
					Showing {startEntry} to {endEntry} of {totalEntries} entries
				</Text>
				{!isMobilePagination && (
					<Select
						value={pageSize}
						onChange={(event) => onPageSizeChange(Number(event.target.value))}
						width='96px'
						height='37px'
						borderRadius='6px'
						border='1px solid #AFA2FE'
						background='#8774FE33'
						color='#292929'
						fontSize='14px'
						fontWeight='500'
						iconColor='#292929'
						_focusVisible={{
							borderColor: '#AFA2FE',
							boxShadow: 'none',
						}}
					>
						{pageSizeOptions.map((option) => (
							<option key={option} value={option}>
								{option} items
							</option>
						))}
					</Select>
				)}
			</Flex>

			<Flex alignItems='center' gap='4px' width='auto' justifyContent='flex-end' flexShrink={0}>
				<PaginationButton
					label={isMobilePagination ? '‹' : '‹ Previous'}
					onClick={(event) => handlePageChange(pageNum - 1, event)}
					isDisabled={pageNum === 1 || isFetching}
					isCompact={isMobilePagination}
				/>
				{paginationItems.map((item, index) =>
					item === 'ellipsis' ? (
						<PaginationButton
							key={`ellipsis-${index}`}
							label='...'
							isDisabled
							isCompact={isMobilePagination}
						/>
					) : (
						<PaginationButton
							key={item}
							label={String(item)}
							onClick={(event) => handlePageChange(item, event)}
							isActive={item === pageNum}
							isDisabled={isFetching}
							isCompact={isMobilePagination}
						/>
					),
				)}
				<PaginationButton
					label={isMobilePagination ? '›' : 'Next ›'}
					onClick={(event) => handlePageChange(pageNum + 1, event)}
					isDisabled={pageNum === totalPages || isFetching}
					isCompact={isMobilePagination}
				/>
			</Flex>
		</Flex>
	);
};

export default DesktopPagination;
